import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Snackbar,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { setUserId } from "../../Redux/actions";
import axios from "axios";
import { Toast } from "primereact/toast";
import bgImage from "../../assets/img/login_bg.svg";
import logoSmartHire from "../../assets/img/logo_smartHire.svg";
import new_logo from "../../assets/img/new_logo.svg";
import { useTranslation } from "react-i18next";
import colors from "colors/color";
import CompanySignUp from "./CompanySignUp";
import { NewPostButton } from "../../components/Buttons/Buttons";
import arrow_back_icon from "../../assets/img/arrow_back_icon.svg";

const CompanyRegistrationPage = (props) => {
  const { t } = useTranslation();
  const toast = useRef(null);
  const navigate = useNavigate();
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const apiUrl = process.env.REACT_APP_API_URL;
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const linkedInClientId = process.env.REACT_APP_LINKEDIN_CLIENT_ID || "86dpwd87p8nanb";
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const handleResize = () => {
      const windowHeight = window.innerHeight;
      const windowWidth = window.innerWidth;
      const contentHeight = 800;
      const contentWidth = 500;

      const heightScale = (windowHeight * 0.9) / contentHeight;
      const widthScale = (windowWidth * 0.9) / contentWidth;

      const newScale = Math.min(heightScale, widthScale, 1);
      setScale(newScale);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  const toggleAuthMode = () => {
    navigate("/login");
  };

  function getUserIdFromAccessToken() {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) return null;

    try {
      const decodedToken = parseJwt(accessToken);
      return decodedToken.user_id;
    } catch (error) {
      console.error("Failed to decode token:", error);
      return null;
    }
  }

  function parseJwt(token) {
    try {
      if (!token || typeof token !== 'string') {
        throw new Error('Invalid token format');
      }

      const parts = token.split(".");
      if (parts.length !== 3) {
        throw new Error('Invalid JWT structure');
      }

      const base64Url = parts[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('JWT parsing error:', error);
      throw error;
    }
  }

  const redirectToAdminA = () => navigate("/manager/ListRecruiter");
  const redirectToDashboard = () => navigate("/talent/dashboard");
  const redirectToSuperAdmin = () => navigate("/superadmin/dashboard");
  const redirectToHome = () => navigate("/landing");

  const handleRoleBasedRedirect = (userRole) => {
    switch (userRole) {
      case "r":
        redirectToDashboard();
        break;
      case "a":
        redirectToAdminA();
        break;
      case "s":
        redirectToSuperAdmin();
        break;
      default:
        redirectToHome();
    }
  };

  const handleSignUpSuccess = () => {
    setSnackbar({
      open: true,
      message: t("registration.success.message"),
      severity: "success",
    });
    setTimeout(() => {
      navigate("/login");
    }, 1500);
  };

  const handleGoogleSuccess = async (res) => {
    try {
      const response = await axios.get(
        `${apiUrl}authentication/google/userinfo/`,
        { withCredentials: true, params: res.profileObj }
      );
      localStorage.setItem("accessToken", response.data.access_token);
      localStorage.setItem("refreshToken", response.data.refresh_token);
      if (response.data.user.image) {
        localStorage.setItem("userImage", response.data.user.image);
      }

      try {
        window.dispatchEvent(new Event('auth-token-changed'));
      } catch (error) {
        console.error('Error dispatching auth-token-changed event:', error);
      }

      const userId = getUserIdFromAccessToken();
      if (userId) {
        try {
          localStorage.setItem('userId', String(userId));
        } catch (error) {
          console.error('Error saving userId to localStorage:', error);
        }
        props.setUserId(userId);
      }

      handleRoleBasedRedirect(response.data.user.role);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleLinkedInAuth = () => {
    window.location.href = `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${linkedInClientId}&redirect_uri=${window.location.origin}/company-registration&state=1234&scope=openid%20email%20profile`;
  };

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{
        overflow: "hidden",
        height: "100vh",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative"
      }}
    >
      <Toast ref={toast} />

      <Grid
        container
        sx={{
          display: "flex",
          height: "100%",
          width: "100%",
          m: 0,
          p: 0,
          position: "relative"
        }}
      >
        {/* Left side - Company Registration form */}
        <Grid
          item
          xs={12}
          sm={12}
          md={6}
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            p: 0,
            height: "100%",
            width: "100%",
            position: "relative",
            overflow: "auto"
          }}
        >
          <Box
            sx={{
              width: "100%",
              height: "100%",
              position: "relative",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              overflowY: "auto",
              px: { xs: 2, sm: 3, md: 4 },
              py: { xs: 2, sm: 3, md: 3 }
            }}
          >
            {isMobile && (
              <Box
                sx={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100%",
                  padding: "20px",
                  zIndex: 1000,
                }}
              >
                <img src={new_logo} alt="logo" style={{ maxWidth: "100%", height: "auto" }} />
              </Box>
            )}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mb: 4, width: '100%' }}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '17px',
                  marginBottom: '30px',
                  width: '100%',
                  cursor: 'pointer'
                }}
                  onClick={() => navigate("/landing")}
                >
                  <img
                    src={logoSmartHire}
                    alt="SMARTHIRE"
                    style={{
                      width: '174px',
                      height: '32px'
                    }}
                  />
                </Box>
                {/* <Box sx={{ mb: 3 }}>
                  <NewPostButton
                    startIcon={<img src={arrow_back_icon} alt="Home" style={{ marginRight: 8 }} />}
                    onClick={() => navigate("/landing")}
                    sx={{
                      minWidth: 120,
                      width: 'auto',
                      padding: '8px 16px',
                      height: '40px',
                      fontWeight: 600,
                      fontFamily: "Poppins",
                      fontSize: "16px",
                      borderRadius: "11px",
                      border: "1px solid #0017E7",
                      color: "#0017E7",
                      background: "#fff",
                      textTransform: "none",
                      boxShadow: "none",
                      '&:hover': {
                        background: '#0017E7',
                        color: '#fff',
                        borderColor: '#0017E7',
                      },
                    }}
                  >
                    {t("navBar.home")}
                  </NewPostButton>
                </Box> */}
                <Typography
                  sx={{
                    color: colors.primary,
                    fontSize: { xs: "32px", sm: "36px", md: "40px" },
                    fontFamily: "Kontora",
                    fontWeight: 700,
                    mb: 0.5,
                    width: { xs: "auto", sm: "auto", md: "auto" },
                    height: { xs: "35px", sm: "40px", md: "45px" },
                    lineHeight: 1,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {t("registration.company")}
                </Typography>
              </Box>
            </Grid>

            <CompanySignUp
              apiUrl={apiUrl}
              toggleAuthMode={toggleAuthMode}
              onSignUpSuccess={handleSignUpSuccess}
              onGoogleSuccess={handleGoogleSuccess}
              onLinkedInAuth={handleLinkedInAuth}
              setSnackbar={setSnackbar}
              clientId={clientId}
            />
          </Box>
        </Grid>

        {/* Right side - Background image (hidden on xs screens) */}
        <Grid
          item
          sx={{
            display: { xs: "none", sm: "block" },
            p: 0,
            height: "100%",
            position: "relative",
            width: { sm: "100%", md: "50%" },
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              bgcolor: "#1976d2",
              color: "white",
              backgroundImage: `url(${bgImage})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            {/* Logo Container */}
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                p: { sm: 3, md: 4 },
                cursor: "pointer"
              }}
              onClick={() => navigate("/landing")}
            >

            </Box>

            {/* Text Container */}
            <Box sx={{ p: { sm: 3, md: 4 } }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 600,
                  fontSize: { sm: "1.5rem", md: "2rem", lg: "2.25rem" },
                  lineHeight: 1.2,
                  fontFamily: "Poppins",
                  pr: { sm: 2, md: 6, lg: 8 },
                }}
              >
                {t("login.slogan")}
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        sx={{
          "& .MuiSnackbarContent-root": {
            backgroundColor: "rgba(255, 255, 255, 0)",
            color: "black",
            borderRadius: "50px",
            backdropFilter: "blur(3px)",
            boxShadow: "0 4px 30px rgba(0, 0, 0, 0.5)",
          },
        }}
        message={snackbar.message}
      />
    </Container>
  );
};

const mapStateToProps = (state) => ({
  userId: state.userId,
});

const mapDispatchToProps = {
  setUserId,
};

export default connect(mapStateToProps, mapDispatchToProps)(CompanyRegistrationPage);
